import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { SITE, IMAGES, API_URL } from "@/lib/site";
import { Reveal, SectionHead } from "@/components/motion";
import BeforeAfter from "@/components/BeforeAfter";
import { useSEO } from "@/components/SEO";

const FILTERS = [
  { id: "tous", label: "Tous" },
  { id: "auto", label: "Auto" },
  { id: "moto", label: "Moto" },
  { id: "atelier", label: "Atelier" },
];

const GALLERY = [
  { src: "/galerie/golf7-1.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-2.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-3.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-4.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-5.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-6.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-7.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: "/galerie/golf7-8.jpg", cat: "auto", caption: "Golf 7 — embrayage & traitement anti-rouille du berceau" },
  { src: IMAGES.hero, cat: "auto", caption: "BMW M3 — la voiture vitrine de l'atelier" },
  { src: IMAGES.vitrineMoto, cat: "moto", caption: "Kawasaki Z750 Black Saphir" },
  { src: IMAGES.atelierReel, cat: "atelier", caption: "L'atelier, vu depuis l'habitacle" },
];

export default function Realisations() {
  useSEO(
    "Réalisations — Avant / Après & galerie atelier | BM MECHANICS Prilly",
    "Galerie des réalisations de l'atelier BM MECHANICS à Prilly : réparations auto, entretien moto et scooter, comparateur avant/après."
  );
  const [filter, setFilter] = useState("tous");
  const [uploaded, setUploaded] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/photos`)
      .then((r) => r.json())
      .then((data) => Array.isArray(data) && setUploaded(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!lightbox) return undefined;
    window.__lenis?.stop();
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.images.length });
      if (e.key === "ArrowLeft") setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length });
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [lightbox]);

  // Les photos d'une même intervention (même légende) forment un album ;
  // les photos "avant" sans paire sont aussi visibles dans la galerie
  const pairedBeforeIds = new Set(uploaded.filter((p) => p.role === "after").map((a) => a.pair_id));
  const albumsMap = {};
  uploaded
    .filter((p) => p.role === "gallery" || (p.role === "before" && !pairedBeforeIds.has(p.pair_id)))
    .forEach((p) => {
      const key = (p.caption || "").trim() || p.id;
      if (!albumsMap[key]) {
        albumsMap[key] = { caption: p.caption || "Réalisation BM MECHANICS", cat: p.category, images: [] };
      }
      albumsMap[key].images.push({ src: p.data, caption: p.caption || "" });
    });
  const albums = Object.values(albumsMap);
  const stockMap = {};
  (filter === "tous" ? GALLERY : GALLERY.filter((g) => g.cat === filter)).forEach((g) => {
    if (!stockMap[g.caption]) stockMap[g.caption] = { caption: g.caption, cat: g.cat, images: [] };
    stockMap[g.caption].images.push({ src: g.src, caption: g.caption });
  });
  const stock = Object.values(stockMap);
  const list = [...albums.filter((a) => filter === "tous" || a.cat === filter), ...stock];

  const realPairs = uploaded
    .filter((p) => p.role === "before")
    .map((b) => {
      const after = uploaded.find((a) => a.role === "after" && a.pair_id === b.pair_id);
      return after ? { pairId: b.pair_id, before: b.data, after: after.data, caption: b.caption } : null;
    })
    .filter(Boolean);

  return (
    <div data-testid="realisations-page">
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-16 md:pt-40 md:pb-20 texture-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">Réalisations</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight leading-[0.95]">
              Le travail parle<br />de lui-même
            </h1>
            <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[#9A968C]">
              Un aperçu du travail de l'atelier. Cette galerie sera enrichie des vraies
              réalisations BM MECHANICS au fil des interventions.
            </p>
          </Reveal>
        </div>
      </section>

      {realPairs.length > 0 && (
      <section className="py-12 md:py-20" data-testid="realisations-beforeafter">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="01" eyebrow="Avant / Après" title="Faites glisser le curseur" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {realPairs.map((p, i) => (
              <Reveal key={p.pairId} delay={i * 0.1}>
                <BeforeAfter before={p.before} after={p.after} testid={`before-after-real-${i}`} />
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D64]">
                  {p.caption || "Réalisation BM MECHANICS"}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      <section className="py-12 md:py-20 bg-[#EFECE4] hairline-t hairline-b" data-testid="realisations-gallery">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead index="02" eyebrow="Galerie" title="L'atelier en images" />
          <div className="flex flex-wrap gap-2 mb-10" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`gallery-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-2.5 border transition-colors ${
                  filter === f.id
                    ? "bg-[#121212] text-[#F6F3EB] border-[#121212]"
                    : "bg-white text-[#4A4D53] border-[#D8D4CA] hover:border-[#121212] hover:text-[#121212]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
            {list.map((g, i) => (
              <Reveal key={`${g.caption}-${i}`} delay={(i % 4) * 0.06}>
                <figure
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setLightbox({ images: g.images, index: 0 })}
                  className="group border border-[#D8D4CA] bg-white p-2 hover:border-[#121212] transition-colors cursor-pointer"
                >
                  <div className="overflow-hidden relative">
                    <img
                      src={g.images[0].src}
                      alt={g.caption}
                      loading="lazy"
                      className="w-full aspect-[4/3] object-cover group-hover:scale-[1.04] transition-transform duration-700"
                    />
                    <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-[#121212]/30">
                      <ZoomIn className="h-6 w-6 text-[#F6F3EB]" />
                    </span>
                    {g.images.length > 1 && (
                      <span className="absolute right-2 top-2 bg-[#121212]/85 text-[#F6F3EB] font-mono text-[10px] uppercase tracking-[0.16em] px-2.5 py-1">
                        {g.images.length} photos
                      </span>
                    )}
                  </div>
                  <figcaption className="px-2 py-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[#5A5D64]">
                    {g.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-[#121212] text-[#F6F3EB] texture-grain" data-testid="realisations-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
              Votre véhicule mérite le même soin
            </h2>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link
                to="/devis"
                data-testid="realisations-quote-btn"
                className="inline-flex items-center gap-2 bg-[#8C7B5D] text-[#121212] font-mono text-xs uppercase tracking-[0.16em] font-medium px-7 py-4 hover:bg-[#F6F3EB] transition-colors"
              >
                Demander un devis <ArrowUpRight className="h-4 w-4" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="realisations-call-btn"
                className="inline-flex items-center gap-2 border border-[#4A4D53] font-mono text-xs uppercase tracking-[0.16em] px-7 py-4 hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[70] bg-[#121212]/95 flex flex-col items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close-btn"
            aria-label="Fermer la visionneuse"
            className="absolute top-4 right-4 h-11 w-11 flex items-center justify-center border border-[#4A4D53] text-[#F6F3EB] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox.images[lightbox.index].src}
            alt={lightbox.images[lightbox.index].caption || "Réalisation BM MECHANICS"}
            className="max-h-[76vh] max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="mt-4 flex items-center gap-6" onClick={(e) => e.stopPropagation()}>
            {lightbox.images.length > 1 && (
              <button
                data-testid="lightbox-prev-btn"
                aria-label="Photo précédente"
                onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }))}
                className="h-11 w-11 flex items-center justify-center border border-[#4A4D53] text-[#F6F3EB] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#9A968C] text-center">
              {lightbox.images[lightbox.index].caption && (
                <span className="text-[#F6F3EB]">{lightbox.images[lightbox.index].caption} · </span>
              )}
              {lightbox.index + 1} / {lightbox.images.length}
            </p>
            {lightbox.images.length > 1 && (
              <button
                data-testid="lightbox-next-btn"
                aria-label="Photo suivante"
                onClick={() => setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.images.length }))}
                className="h-11 w-11 flex items-center justify-center border border-[#4A4D53] text-[#F6F3EB] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
